import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { HelperService } from '../../../shared/services/helper.service';
import { SearchService } from '../../../shared/services/search.service';
import { PreventiveTreatmentType } from '../../animals/models/medical/medical.enums';
import { AnimalSearchFilters } from '../../animals/states/animal.repository';

/**
 * Die Felder, die diese Karte verwaltet. Der Rest der erweiterten Suche liegt weiter in
 * `dashboard-search`; beide schreiben in denselben Filter-Satz im `SearchService`.
 */
const MEDICAL_KEYS = [
  'medicalFinding',
  'veterinarian',
  'medicalDate',
  'medicalCondition',
  'treatmentType',
  'treatmentPreparation',
  'treatmentDueAt',
  'quarantineUntil',
] as const satisfies readonly (keyof AnimalSearchFilters)[];

type MedicalKey = typeof MEDICAL_KEYS[number];
type MedicalFilters = Partial<Record<MedicalKey, string>>;

/**
 * Medizinische Tiersuche auf dem Dashboard. Die Ergebnisse zeigt die bestehende Suchkarte an —
 * beide Karten teilen sich die Filter über den `SearchService`.
 */
@Component({
  selector: 'app-dashboard-medical-search',
  templateUrl: './dashboard-medical-search.component.html',
  standalone: false
})
export class DashboardMedicalSearchComponent implements OnInit, OnDestroy {
  open = false;
  filters: MedicalFilters = {};
  treatmentTypeOptions: { name: string; value: string }[] = [];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _searchService: SearchService,
    private readonly _helperService: HelperService
  ) {}

  ngOnInit(): void {
    this.treatmentTypeOptions = this._helperService.getEnumOptions(
      PreventiveTreatmentType,
      'animalMedical.enum.treatmentType',
      true
    );

    // Mitziehen, wenn die andere Karte die Filter zurücksetzt.
    this._searchService.searchFilters$.pipe(takeUntil(this._destroy$)).subscribe(filters => {
      this.filters = this.pickMedical(filters ?? {});
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  toggle(): void {
    this.open = !this.open;
  }

  get hasValues(): boolean {
    return MEDICAL_KEYS.some(key => (this.filters[key] ?? '').trim().length > 0);
  }

  /** Eigene Felder in den geteilten Filter-Satz mischen, fremde Felder unangetastet lassen. */
  apply(): void {
    this.push(this.filters);
  }

  reset(): void {
    this.filters = {};
    this.push({});
  }

  private push(medical: MedicalFilters): void {
    const current = { ...this._searchService.searchFiltersSubject.value } as AnimalSearchFilters;

    for (const key of MEDICAL_KEYS) {
      const value = (medical[key] ?? '').trim();
      if (value) {
        (current as Record<string, unknown>)[key] = value;
      } else {
        delete (current as Record<string, unknown>)[key];
      }
    }

    this._searchService.paginationSubject.next({ first: 0, page: 0, rows: 6 });
    this._searchService.searchFiltersSubject.next(current);
  }

  private pickMedical(filters: AnimalSearchFilters): MedicalFilters {
    const result: MedicalFilters = {};
    for (const key of MEDICAL_KEYS) {
      const value = filters[key];
      if (typeof value === 'string' && value.trim()) result[key] = value;
    }
    return result;
  }
}
